import React, { useCallback, useMemo } from 'react';

import styles from './ScheduleHistory.module.scss';
import Table from '../../../components/Table';
import { useTranslation } from 'react-i18next';
import { formatDate, formatTime } from '../../../utils';
import Pagination from '../../../components/Pagination';
import UserInfoSmall from '../../../components/UserInfoSmall';
import { useGetLessonsQuery } from '../../../store/apis/lessons/scheduled';
import useQueryString from '../../../hooks/useQueryString';
import ContentLoader from 'react-content-loader';
import { Link, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import ContentEmpty from '../../../components/ContentEmpty';
import appRoutes from '../../../app/appRoutes';

const ScheduleHistory = () => {
  const [t, i18n] = useTranslation();
  const navigate = useNavigate();
  const {
    page = 1,
    pageSize = 20,
    state = 2,
    ...restQuery
  } = useQueryString();

  const { data: lessons, isLoading, isFetching } = useGetLessonsQuery({ page, pageSize, state, ...restQuery });

  const columns = useMemo(() => [
    {
      id: 3,
      accessor: 'tutor',
      Header: t('Учитель'),
      Cell: ({ value }) => (
        <Link to={appRoutes.tutors(value.id)} className={styles.tableLink}>
          <UserInfoSmall
            photoUrl={value.photoUrl}
            name={value.fullName?.[i18n.language]}
            message={value.shortInfo[i18n.language]}
          />
        </Link>
      )
    },
    {
      id: 2,
      accessor: 'subject',
      Header: t('Предмет'),
    },
    {
      id: 1,
      accessor: 'date',
      Header: t('Дата урока'),
      Cell: ({ value }) => formatDate(value),
    },
    {
      id: 11,
      accessor: 'date',
      Header: t('Время урока'),
      Cell: ({ value }) => formatTime(value),
    },
    {
      id: 4,
      accessor: 'state',
      Header: t('Состояние'),
    },
  ], [i18n.language, t]);

  const changePage = useCallback((page) => {
    navigate({
      pathname: '',
      search: queryString.stringify({ page, ...restQuery }),
    });
  }, [navigate, restQuery]);

  const goToTutorsSearch = useCallback(() => {
    navigate(appRoutes.tutors());
  }, [navigate]);

  if (isLoading) return (
    <ContentLoader
      backgroundColor={'#c8c8c8'}
      foregroundColor={'#eceaea'}
      width="100%"
      height="340"
      style={{ padding: '0', boxSizing: 'border-box' }}
      speed={2}
    >
      {[0, 1, 2, 3, 4, 5].map((id) => {
        const height = 48;
        const positionY = id * (height + 10);
        return (
          <rect x="0" y={positionY} rx="6" ry="6" width="100%" height={height} key={id} />
        )
      })}
    </ContentLoader>
  );

  if (lessons && lessons.results.length === 0) return (
    <ContentEmpty
      imgSrc="/online-app/images/empty-sleeping.png"
      title={t('У Вас пока не было ни одного пройденного занятия')}
      subtitle={t('Здесь будет отображаться история Ваших пройденных занятий')}
      actionText={t('Хотите найти репетитора?')}
      onAction={goToTutorsSearch}
      btnClasses="Button_button__JBBzO_Find"
    />
  )

  const data = lessons?.results.map((lesson) => ({
    id: lesson.id,
    date: lesson.lessonTime,
    subject: lesson.subject?.shortName[i18n.language],
    tutor: lesson.tutor,
    state: lesson.state?.name[i18n.language],
  }));

  return (
    <>
      <div className={styles.history}>
        <Table
          columns={columns}
          data={data}
          loading={isFetching}
        />
      </div>
      <div className={styles.historyFooter}>
        <Pagination
          count={lessons?.totalPages}
          currentPage={page}
          onPageChange={changePage}
        />
      </div>
    </>
  );
};

export default ScheduleHistory;
